import { updateMaintenanceSetting } from "@/api/services/admin/system-setting";
import { FooterActionBar } from "@/components/commons/FooterActionBar";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { InputDateTimePicker } from "@/components/elements/InputDateTimePicker";
import { Select } from "@/components/elements/Select";
import { Textarea } from "@/components/elements/Textarea";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { maintenanceSchema } from "@/features/system-setting/schema/maintenanceSchema";
import { TMaintenanceForm } from "@/features/system-setting/types/maintenanceForm";
import { convertMaintenanceSettingFormToUpdateReq } from "@/features/system-setting/utils/converts";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { divider, text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

const initForm: TMaintenanceForm = {
  maintenanceMode: "",
  modeStartDateTime: undefined,
  modeEndDateTime: undefined,
  maintenanceMessage: "",
};

type TMaintenanceFormProps = {
  readonly?: boolean;
  previousData?: Partial<TMaintenanceForm>;
};

export const MaintenanceForm: FC<TMaintenanceFormProps> = ({ readonly, previousData }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const methods = useForm<TMaintenanceForm>({
    defaultValues: previousData ?? initForm,
    resolver: yupResolver(maintenanceSchema),
    mode: "onBlur",
  });
  const { control } = methods;

  const onSubmit = async (data: TMaintenanceForm) => {
    try {
      const payload = convertMaintenanceSettingFormToUpdateReq(data);
      const rs = await updateMaintenanceSetting(payload);
      navigate(AppRoutes.setting);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SYSTEM_SETTING.FETCH_MAINTENANCE_DETAIL] });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={maintenanceSchema}>
      <InnerBox
        padding="24px"
        width="100%"
        sx={{
          border: `1px solid ${divider.middle}`,
          borderRadius: "0px",
          borderBottomLeftRadius: "8px",
          borderBottomRightRadius: "8px",
        }}
      >
        <Stack gap={3}>
          <Typography variant="sub16Semi">メンテナンスツール</Typography>
          <Typography variant="body14" color={text.accent}>
            日時を設定後、切替日のAM9:00に重要なお知らせとして画面上部に表示されます。
            <br />
            このお知らせはメールが自動配信されないため、緊急メンテナンス時は事前にお知らせしてください。
          </Typography>
          <Grid container spacing={3} maxWidth={814}>
            <Grid item xs={6}>
              <Stack gap={3}>
                <FormField control={control} name="maintenanceMode">
                  <Select enumName="EMaintenanceMode" />
                </FormField>
              </Stack>
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
              <Stack gap={3}>
                <FormField control={control} name="modeStartDateTime">
                  <InputDateTimePicker timezone="Asia/Tokyo" />
                </FormField>
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <Stack gap={3}>
                <FormField control={control} name="modeEndDateTime">
                  <InputDateTimePicker timezone="Asia/Tokyo" />
                </FormField>
              </Stack>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="maintenanceMessage">
              <Textarea />
            </FormField>
          </Grid>
        </Stack>
      </InnerBox>
      <FooterActionBar mt={4}>
        {!readonly && (
          <Box textAlign="right">
            <Button size="sm" type="submit">
              設定
            </Button>
          </Box>
        )}
      </FooterActionBar>
    </Form>
  );
};
